import React, { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  FormControl,
  FormError,
  FormLabel,
  Heading,
  Input,
} from '@alefeducation/ui-elements-library-core';
import { UserType } from '../../pages/AddMember/AddMember';
import { isPasswordStrong } from '../../utils/Validators';
import {
  CHECK_EMAIL,
  CONFIRM_PASSWORD,
  CONTACT_EMAIL,
  EMAIL_EXISTS,
  FIRST_NAME,
  LAST_NAME,
  PASSWORD,
  PASSWORD_MISMATCH_ERROR_MESSAGE,
  WEAK_PASSWORD_ERROR_MESSAGE,
} from '../../utils/constants';
import { FEATURE_TOGGLE } from '../../utils/FeatureToggle';
import { flexStyle, StyledTableSection } from '../CommonStyles/TableStyles';
import ResetPassword from '../ResetPassword/ResetPassword';
import InfoMessage from '../InfoMessage';

const StyledRow = styled.div`
  display: flex;
  column-gap: 24px;
`;

const StyledFormControl = styled(FormControl)`
  display: flex;
  flex-direction: column;
  width: 40%;

  .check-email-btn {
    width: 100px;
  }
`;

const StyledGeneralInfoElements = styled.div`
  ${flexStyle};

  .btn-update-member-v2 {
    align-self: flex-end;
  }
`;

const StyledInputAction = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 30px;
  gap: ${({ theme }: any) => `${theme.space[16]}px`};
`;

interface Props {
  isOrgMember?: boolean;
  isEdit?: boolean;
  userType?: UserType;
  disabled?: boolean;
  messageText?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  password?: string;
  confirmPass?: string;
  isEmailChanged?: boolean;

  onNameChange?(e: ChangeEvent<HTMLInputElement>): void;

  onEmailChange?(e: any): void;

  setIsEmailChanged?(status: boolean): void;

  onCheckEmail?(): void;

  onFirstNameChange?(e: ChangeEvent<HTMLInputElement>): void;

  onLastNameChange?(e: ChangeEvent<HTMLInputElement>): void;

  onPassChange?(e: ChangeEvent<HTMLInputElement>): void;

  onConfirmPassChange?(e: ChangeEvent<HTMLInputElement>): void;
}

const GeneralInformation: React.FC<Props> = ({
  isOrgMember = false,
  isEdit,
  isEmailChanged,
  messageText,
  userType,
  disabled,
  name,
  firstName,
  lastName,
  email,
  onNameChange,
  onEmailChange,
  setIsEmailChanged,
  onFirstNameChange,
  onLastNameChange,
  onPassChange,
  onConfirmPassChange,
  onCheckEmail,
  password,
  confirmPass,
}) => {
  const [hasPassError, setPassError] = useState<boolean>(false);
  const [isPasswordWeak, setWeakPassError] = useState<boolean>(false);

  const onEmailUpdate = (e: any) => {
    setIsEmailChanged?.(true);
    onEmailChange?.(e);
  };

  const isNewUser =
    userType === UserType.NEW || userType === UserType.ALEF_ONLY;
  const renderResetPassEnabled =
    FEATURE_TOGGLE.ENABLE_PASSWORD_RESET_LINK && !isOrgMember;

  return (
    <StyledTableSection>
      <Heading
        data-testid={'general-info-heading'}
        variant="heading-small"
        color={disabled ? 'primary.40' : 'text.100'}
      >
        {isEdit
          ? '1. General Information'
          : '2. Please add general information'}
      </Heading>
      {!disabled && (
        <StyledGeneralInfoElements data-testid="general-info-elements">
          <StyledRow>
            {!FEATURE_TOGGLE.ENABLE_AUTH0_INTEGRATION && (
              <StyledFormControl>
                <FormLabel data-testid={'member-name-label'}>Name</FormLabel>
                <Input
                  disabled={!isNewUser}
                  name="name"
                  placeholder="Enter member name"
                  value={name || ''}
                  onChange={onNameChange}
                  data-testid={'member-name'}
                />
              </StyledFormControl>
            )}

            <StyledFormControl>
              <FormLabel data-testid={'member-email-label'}>
                {CONTACT_EMAIL}
              </FormLabel>
              <Input
                value={email}
                type="email"
                placeholder="user email"
                disabled={isOrgMember}
                data-testid={'member-email'}
                onChange={onEmailUpdate}
              />
            </StyledFormControl>
            {isEdit && !isOrgMember && (
              <StyledFormControl>
                <StyledInputAction>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!isEmailChanged}
                    onClick={onCheckEmail}
                    className="check-email-btn"
                    data-testid={'check-email-button'}
                  >
                    {CHECK_EMAIL}
                  </Button>
                  {messageText && <InfoMessage messageText={EMAIL_EXISTS} />}
                </StyledInputAction>
              </StyledFormControl>
            )}
          </StyledRow>
          {FEATURE_TOGGLE.ENABLE_AUTH0_INTEGRATION && (
            <StyledRow>
              <StyledFormControl>
                <FormLabel data-testid={'member-first-name-label'}>
                  {FIRST_NAME}
                </FormLabel>
                <Input
                  disabled={!isNewUser}
                  name="firstName"
                  placeholder="Enter member first name"
                  value={firstName || ''}
                  onChange={onFirstNameChange}
                  data-testid={'member-first-name'}
                />
              </StyledFormControl>
              <StyledFormControl>
                <FormLabel data-testid={'member-last-name-label'}>
                  {LAST_NAME}
                </FormLabel>
                <Input
                  disabled={!isNewUser}
                  name="lastName"
                  placeholder="Enter member last name"
                  value={lastName || ''}
                  onChange={onLastNameChange}
                  data-testid={'member-last-name'}
                />
              </StyledFormControl>
            </StyledRow>
          )}
          {isNewUser && (
            <StyledRow data-testid="password-container">
              <StyledFormControl
                appearance={isPasswordWeak ? 'error' : 'regular'}
              >
                <FormLabel data-testid={'password-label'}>{PASSWORD}</FormLabel>
                <Input
                  name="password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={onPassChange}
                  data-testid={'password'}
                  onBlur={() => {
                    if (password) {
                      setWeakPassError(!isPasswordStrong(password));
                    }
                  }}
                />
                {isPasswordWeak && (
                  <FormError data-testid={'weak-password'}>
                    {WEAK_PASSWORD_ERROR_MESSAGE}
                  </FormError>
                )}
              </StyledFormControl>
              <StyledFormControl
                appearance={hasPassError ? 'error' : 'regular'}
              >
                <FormLabel data-testid={'confirm-password-label'}>
                  {CONFIRM_PASSWORD}
                </FormLabel>
                <Input
                  name="confirm-password"
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPass}
                  onChange={onConfirmPassChange}
                  onBlur={() => {
                    setPassError(confirmPass !== password);
                  }}
                  data-testid={'confirm-password'}
                />
                {hasPassError && (
                  <FormError data-testid={'password-mismatch-error'}>
                    {PASSWORD_MISMATCH_ERROR_MESSAGE}
                  </FormError>
                )}
                {renderResetPassEnabled && <ResetPassword email={email} />}
              </StyledFormControl>
            </StyledRow>
          )}
        </StyledGeneralInfoElements>
      )}
    </StyledTableSection>
  );
};

export default GeneralInformation;
